import React, {
  forwardRef, useCallback, useMemo,
} from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  CloseIcon, Dragndrop, TriangleArrowDown,
} from '@picsio/icons';
import { IconButton, Popper } from '@picsio/ui';
import { useSelector } from 'react-redux';

import SearchFilterDropOpener from './SearchFilterDropOpener';
import { MULTIPLE_OPERATORS, SINGLE_OPERATORS } from './config';
import Tooltip from '../Tooltip';
import { getColorLabelDisplayName } from '../../helpers/colorsHelper';
import getSearchFiltersDialogMountParent from '../../helpers/getSearchFiltersDialogMountParent';
import { selectColorLabels } from '../../store/selectors/colorLabels';
import getBaseTitle from './helpers/getBaseTitle';
import getDateTitle from './helpers/getDateTitle';
import getRatingTitle from './helpers/getRatingTitle';
import getSizeTitle from './helpers/getSizeTitle';
import getArrayValueTitle from './helpers/getArrayValueTitle';
import getComplexArrayValueTitle from './helpers/getComplexArrayValueTitle';
import getBooleanTitle from './helpers/getBooleanTitle';
import getNumberTitle from './helpers/getNumberTitle';
import getCollectionTitle from './helpers/getCollectionTitle';
import normalizeDate from './helpers/normalizeDate';
import normalizeText from './helpers/normalizeText';

const resolveColorFilterDisplayValue = (item, colorLabels) => (
  typeof item === 'string' ? getColorLabelDisplayName(item, colorLabels) : item
);

const SearchFilterItem = forwardRef(({
  id = '',
  disabled,
  data,
  draggable = true,
  removable = true,
  onDragStart,
  onDragEnter,
  onDragEnd,
  customizingMode,
  isSelected,
  isOpen,
  onOpen,
  onClose,
  onSelectFilter,
  onResetFilter,
  onRemove,
  filterData,
  onConfirmFilter,
  confirmedData = {},
  filters,
  rootCollectionId,
  stackAboveModals = false,
}, ref) => {
  const {
    title, type, subtype, operators, options,
  } = data;
  const colorLabels = useSelector(selectColorLabels);
  const isColorFilter = id === 'color' || type === 'color';
  const displayConfirmedData = useMemo(() => {
    if (!isColorFilter) return confirmedData;

    return {
      ...confirmedData,
      value: Array.isArray(confirmedData.value)
        ? confirmedData.value.map((item) => resolveColorFilterDisplayValue(item, colorLabels))
        : resolveColorFilterDisplayValue(confirmedData.value, colorLabels),
      excludedValues: Array.isArray(confirmedData.excludedValues)
        ? confirmedData.excludedValues.map((item) => resolveColorFilterDisplayValue(item, colorLabels))
        : confirmedData.excludedValues,
    };
  }, [colorLabels, confirmedData, isColorFilter]);

  const operatorName = useMemo(() => {
    if (id.startsWith('meta.')) {
      if (type === 'number') {
        return filters?.meta?.operatorNamesNumber[displayConfirmedData?.operator];
      }
      if (type === 'date') {
        return filters?.meta?.operatorNamesDate[displayConfirmedData?.operator];
      }
      return filters?.meta?.operatorNamesString[displayConfirmedData?.operator];
    }
    if (filters[id]?.operatorNames) {
      return filters[id]?.operatorNames[displayConfirmedData?.operator];
    }

    return displayConfirmedData?.operator;
  }, [displayConfirmedData?.operator, filters, id, type]);

  const formattedSelectedFilterTooltip = useMemo(() => {
    const titleToRender = title.startsWith('meta.') ? title.replace('meta.', '') : title;
    if (isSelected) {
      let selectedValueTitle = '';
      if (Array.isArray(displayConfirmedData.value) && !SINGLE_OPERATORS.includes(displayConfirmedData.operator)) {
        if (displayConfirmedData.value[0]?.name) {
          selectedValueTitle = displayConfirmedData.value.map((item) => normalizeText(item.name)).join(', ');
        } else if (displayConfirmedData.value[0]?.title) {
          selectedValueTitle = displayConfirmedData.value.map((item) => normalizeText(item.title)).join(', ');
        } else if (displayConfirmedData.value[0]?.displayName) {
          selectedValueTitle = displayConfirmedData.value.map((item) => normalizeText(item.displayName)).join(', ');
        } else {
          selectedValueTitle = displayConfirmedData.value.map((item) => normalizeText(item)).join(', ');
        }
        if (id === 'fileSize') {
          return `${titleToRender} (${operatorName}) ${getSizeTitle({ operator: displayConfirmedData.operator, value: displayConfirmedData.value, title: titleToRender })}`;
        }
        if (type === 'number' || type === 'float') {
          return `${titleToRender} (${operatorName}) ${getNumberTitle({ operator: displayConfirmedData.operator, value: displayConfirmedData.value, title: titleToRender })}`;
        }
        if (type === 'rating') {
          return `${titleToRender} (${operatorName}) ${getRatingTitle({ operator: displayConfirmedData.operator, value: displayConfirmedData.value, title: titleToRender })}`;
        }
        if (!MULTIPLE_OPERATORS.includes(displayConfirmedData.operator)) {
          return `${titleToRender} (${operatorName}) ${selectedValueTitle}`;
        }
      }
      if (MULTIPLE_OPERATORS.includes(displayConfirmedData.operator)) {
        if (!operatorName) return '';
        const formattedOperatorTitle = operatorName?.split('...');
        let excludedValueTitle = '';
        if (displayConfirmedData.excludedValues && displayConfirmedData.excludedValues.length > 0) {
          if (displayConfirmedData.excludedValues[0]?.name) {
            excludedValueTitle = displayConfirmedData.excludedValues.map((item) => normalizeText(item.name)).join(', ');
          } else if (displayConfirmedData.excludedValues[0]?.title) {
            excludedValueTitle = displayConfirmedData.excludedValues.map((item) => normalizeText(item.title)).join(', ');
          } else if (displayConfirmedData.excludedValues[0]?.displayName) {
            excludedValueTitle = displayConfirmedData.excludedValues.map((item) => normalizeText(item.displayName)).join(', ');
          } else {
            excludedValueTitle = displayConfirmedData.excludedValues.map((item) => normalizeText(item)).join(', ');
          }
          if (selectedValueTitle.length === 0) {
            return `${titleToRender} (${formattedOperatorTitle[1]?.trim()}) ${excludedValueTitle}`;
          }
          if (excludedValueTitle.length === 0) {
            return `${titleToRender} (${operatorName}) ${selectedValueTitle}`;
          }
          return `${titleToRender} (${formattedOperatorTitle[0]}) ${selectedValueTitle} (${formattedOperatorTitle[1]?.trim()}) ${excludedValueTitle}`;
        }
        return `${titleToRender} (${formattedOperatorTitle[0]}) ${selectedValueTitle}`;
      }

      if (type === 'date') {
        selectedValueTitle = new Date(displayConfirmedData.value).toLocaleDateString();

        if (displayConfirmedData.operator === 'between') {
          const dates = displayConfirmedData.value.split('_');
          selectedValueTitle = `${normalizeDate(dates[0])} - ${normalizeDate(dates[1], true)}`;
        } else {
          selectedValueTitle = normalizeDate(displayConfirmedData.value);
        }

        const cleanedTitle = titleToRender.replace(/\bat\b/g, '').trim();
        return `${cleanedTitle} ${operatorName} ${SINGLE_OPERATORS.includes(displayConfirmedData.operator) ? '' : selectedValueTitle}`;
      }
      if (type === 'boolean' && !SINGLE_OPERATORS.includes(displayConfirmedData.operator)) { // PCS-8431 - statuses should be as (Trashed Status: Trashed) and so on.
        selectedValueTitle = options.find((option) => option.value === displayConfirmedData.value)?.label;
        return `${titleToRender}: ${selectedValueTitle}`;
      }
      return `${titleToRender} (${operatorName}) ${SINGLE_OPERATORS.includes(displayConfirmedData.operator) ? '' : normalizeText(displayConfirmedData.value)}`;
    }
    return titleToRender;
  }, [title, isSelected, displayConfirmedData, type, operatorName, id, options]);

  const formattedSelectedFilterTitle = useMemo(() => {
    const titleToRender = title.startsWith('meta.') ? title.replace('meta.', '') : title;

    if (!isSelected) {
      return <span className="searchFilterItem__subtitle">{titleToRender}</span>;
    }

    const filterTypeMap = {
      options: () => (
        <span data-type="options" className="searchFilterItem__subtitle">
          {getBaseTitle({
            text: options.find((option) => option.value === displayConfirmedData.value)?.label,
            operator: displayConfirmedData.operator,
            isSingle: true,
          })}
        </span>
      ),
      fileSize: () => (
        <span className="searchFilterItem__subtitle">
          {getSizeTitle({ operator: displayConfirmedData.operator, value: displayConfirmedData.value, title: titleToRender })}
        </span>
      ),
      date: () => (
        <span className="searchFilterItem__subtitle">
          {getDateTitle({ operator: displayConfirmedData.operator, value: displayConfirmedData.value, title: titleToRender })}
        </span>
      ),
      boolean: () => {
        const selectedValueTitle = options.find((option) => option.value === displayConfirmedData.value)?.label;
        return (
          <span data-type="boolean" className="searchFilterItem__subtitle">
            {getBooleanTitle({
              text: titleToRender, operator: displayConfirmedData.operator, value: displayConfirmedData.value, label: selectedValueTitle,
            })}
          </span>
        );
      },
      rating: () => (
        <span className="searchFilterItem__subtitle">
          {getRatingTitle({ operator: displayConfirmedData.operator, value: displayConfirmedData.value, title: titleToRender })}
        </span>
      ),
      number: () => (
        <span className="searchFilterItem__subtitle">
          {getNumberTitle({ operator: displayConfirmedData.operator, value: displayConfirmedData.value, title: titleToRender })}
        </span>
      ),
      collection: () => getCollectionTitle({ confirmedData: displayConfirmedData, rootCollectionId }),
      array: () => getArrayValueTitle(displayConfirmedData),
      complexArray: () => getComplexArrayValueTitle(displayConfirmedData),
      default: () => (
        <span data-type="default" className="searchFilterItem__subtitle">
          {SINGLE_OPERATORS.includes(displayConfirmedData.operator)
            ? `${titleToRender} ${operatorName?.replace(/\bis\b/g, '').trim()}`
            : getBaseTitle({ text: displayConfirmedData.value, operator: displayConfirmedData.operator, isSingle: true })}
        </span>
      ),
    };

    if (id === 'fileSize') return filterTypeMap.fileSize();
    if (type === 'productLayer') return filterTypeMap.array();
    if (type === 'date') return filterTypeMap.date();
    if (type === 'boolean') return filterTypeMap.boolean();
    if (type === 'rating') return filterTypeMap.rating();
    if (type === 'number') return filterTypeMap.number();
    if (type === 'float') return filterTypeMap.number();
    if (type === 'collectionIds') return filterTypeMap.collection();
    if (MULTIPLE_OPERATORS.includes(displayConfirmedData.operator)) {
      return filterTypeMap.complexArray();
    }
    if (Array.isArray(displayConfirmedData.value) && !SINGLE_OPERATORS.includes(displayConfirmedData.operator)) {
      return filterTypeMap.array();
    }
    if (options) return filterTypeMap.options();

    return filterTypeMap.default();
  }, [displayConfirmedData, isSelected, operatorName, title, type, options, id, rootCollectionId]);

  const handleOpen = useCallback(() => (customizingMode ? Function.prototype : onOpen(id)), [customizingMode, id, onOpen]);

  const handleRemove = useCallback(() => onRemove(id), [id, onRemove]);

  const handleResetFilter = useCallback((e) => {
    e.stopPropagation();
    onResetFilter(id);
  }, [id, onResetFilter]);

  const active = useMemo(() => {
    if (isSelected && !customizingMode) {
      if (id === 'collectionIds' && confirmedData?.value?.length === 1 && confirmedData?.value[0]._id === rootCollectionId) {
        return false;
      }
      return true;
    }
    if (isOpen && !customizingMode) {
      return true;
    }
    return false;
  }, [confirmedData?.value, customizingMode, id, isOpen, isSelected, rootCollectionId]);

  const handleChipHover = (e, shouldAdd = true) => {
    if (!active) {
      e.currentTarget.classList.remove('is-hover');
      return;
    }
    const isIcon = e.target.closest('.searchFilterItem__resetIcon');
    e.currentTarget.classList[isIcon || !shouldAdd ? 'remove' : 'add']('is-hover');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (isOpen) {
        onClose(e);
      } else {
        handleOpen(e);
      }
    }
  };

  return (
    <div
      key={id}
      ref={ref}
      data-id={id}
      draggable={!disabled && draggable && removable && customizingMode}
      style={{
        position: 'relative', display: 'inline-block', marginBottom: '4px', marginTop: '4px',
      }}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragEnd={onDragEnd}
    >

      <div
        className={cn('searchFilterItem', {
          settingMode: customizingMode,
          active,
          checked: confirmedData.value,
        })}
        role="button"
        tabIndex={0}
        onClick={isOpen ? onClose : handleOpen}
        onMouseOver={(e) => handleChipHover(e, true)}
        onMouseOut={(e) => handleChipHover(e, false)}
        onFocus={(e) => handleChipHover(e, true)}
        onBlur={(e) => handleChipHover(e, false)}
        onKeyDown={handleKeyDown}
      >
        <If condition={customizingMode && draggable && removable}>
          <div className="searchFilterItem__section">
            <Dragndrop className="searchFilterItem__dnd" />
          </div>
        </If>
        <Tooltip placement="top" content={isSelected ? formattedSelectedFilterTooltip : ''}>
          <div
            className={cn('searchFilterItem__title')}
          >
            { formattedSelectedFilterTitle }
          </div>
        </Tooltip>
        <div className="searchFilterItem__section">
          <Choose>
            <When condition={customizingMode}>
              <If condition={removable}>
                <IconButton size="sm" disabled={disabled} className="searchFilterItem__iconBtn" onClick={handleRemove}>
                  <CloseIcon className="searchFilterItem__closeIcon" />
                </IconButton>
              </If>
            </When>
            <Otherwise>
              <Choose>
                <When condition={active && confirmedData.value}>
                  <Tooltip placement="top" content="Reset filter">
                    <IconButton className="searchFilterItem__resetIcon" color="primary" size="xs" disabled={disabled} onClick={handleResetFilter}>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </When>
                <Otherwise>
                  <IconButton size="sm" disabled={disabled} className="searchFilterItem__iconBtn">
                    <TriangleArrowDown />
                  </IconButton>
                </Otherwise>
              </Choose>
            </Otherwise>
          </Choose>
        </div>
      </div>
      <If condition={ref}>
        <Popper
          target={ref}
          className="dropdownElementPopper"
          isOpen={isOpen}
          offset={[0, 10]}
          hide={false}
          arrow={false}
          autoWidth
          outsideClickListener
          portalContainer={stackAboveModals
            ? getSearchFiltersDialogMountParent(true)
            : undefined}
          onClose={() => {}}
        >
          <SearchFilterDropOpener
            btnRef={ref}
            id={id}
            type={type}
            subtype={subtype}
            selectedFilterData={filterData}
            confirmedData={confirmedData}
            isSelected={isSelected}
            isDropdownOpened={isOpen}
            operators={operators}
            options={options}
            data={data}
            filters={filters}
            title={title}
            stackAboveModals={stackAboveModals}
            onSelectFilter={onSelectFilter}
            onConfirmFilter={onConfirmFilter}
            onResetFilter={onResetFilter}
            onCloseDropdown={onClose}
          />
        </Popper>
      </If>
    </div>
  );
});

SearchFilterItem.propTypes = {
  id: PropTypes.string,
  disabled: PropTypes.bool,
  data: PropTypes.shape({
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    subtype: PropTypes.string,
    operators: PropTypes.arrayOf(PropTypes.string),
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      label: PropTypes.string,
    })),
  }).isRequired,
  draggable: PropTypes.bool,
  removable: PropTypes.bool,
  onDragStart: PropTypes.func,
  onDragEnter: PropTypes.func,
  onDragEnd: PropTypes.func,
  customizingMode: PropTypes.bool,
  isSelected: PropTypes.bool,
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onSelectFilter: PropTypes.func,
  onResetFilter: PropTypes.func,
  onRemove: PropTypes.func,
  filterData: PropTypes.object,
  onConfirmFilter: PropTypes.func,
  confirmedData: PropTypes.shape({
    operator: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
    ]),
  }),
  filters: PropTypes.object,
  rootCollectionId: PropTypes.string,
  stackAboveModals: PropTypes.bool,
};

export default SearchFilterItem;
